import { useApolloClient } from '@apollo/client'
import { Field, Form, Formik } from 'formik'
import { useRouter } from 'next/router'
import React, { useState } from 'react'
import { ROUTE } from 'shared-constants'
import * as Yup from 'yup'
import { z } from 'zod'
import { Button, BUTTON_VARIANT } from 'components/Button'
import ClientSignIn from 'components/ClientSignIn'
import Icons from 'components/Icon'
import { Input } from 'components/Input'
import { Box } from 'components/Layout'
import { Text } from 'components/Typography'
import { useUserAuth } from 'providers/userAuth'
import { PROVIDER_METHODS } from 'providers/userAuthUtil'
import { COLOR_INTENT, COLOR_NAMES, FONT_SIZE, SPACE } from 'Theme'
import { isExpertApplicationEmailValid } from 'utils/validation'

export const AddToFavoritesClientSignUp = ({
  setIsModalOpen,
}: {
  setIsModalOpen: React.Dispatch<React.SetStateAction<boolean>>
}) => {
  const userAuth = useUserAuth()
  const apolloClient = useApolloClient()
  const router = useRouter()
  const [isSignInActive, setIsSignInActive] = useState(false)

  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .email('Invalid email')
      .trim()
      .label('Email')
      .test(
        'valid-email',
        '',
        // eslint-disable-next-line func-names
        async function (value) {
          const { path, createError } = this

          const isValidEmail = z.string().email().safeParse(value).success
          if (!isValidEmail) {
            return createError({
              path,
              message: 'Invalid email format',
            })
          }

          const doesExists = await isExpertApplicationEmailValid({
            email: value,
            apolloClient,
            omitRejected: true,
          })

          if (!doesExists) {
            return createError({
              path,
              message: 'This user already has an account',
            })
          }

          return true
        }
      )
      .required(),
  })

  return (
    <Box>
      {isSignInActive ? (
        <Box>
          <ClientSignIn />
          <Text
            color={COLOR_INTENT.SIGN_IN.TEXT}
            fontSize={FONT_SIZE.PX_12}
            mt={SPACE.PX_15}
            textAlign="center"
            as="p"
          >
            Don&apos;t have an account?{' '}
            <Text
              as="span"
              role="button"
              color={COLOR_NAMES.PINEY_LAKE}
              fontSize={FONT_SIZE.PX_12}
              onClick={() => setIsSignInActive(false)}
              style={{ cursor: 'pointer', textDecoration: 'underline' }}
            >
              Sign up.
            </Text>
          </Text>
        </Box>
      ) : (
        <Box>
          <Formik
            initialValues={{
              firstName: '',
              lastName: '',
              password: '',
              email: '',
            }}
            validationSchema={validationSchema}
            validateOnChange={false}
            onSubmit={({ email }, { resetForm }) => {
              router.push(ROUTE.SIGN_UP({ email, returnUrl: router.asPath }))
              setIsModalOpen(false)
              resetForm()
            }}
          >
            {({ handleSubmit, values }) => (
              <Form onSubmit={handleSubmit}>
                <Box mt={SPACE.PX_24}>
                  <Button
                    onClick={() =>
                      userAuth.thirdPartySignIn(PROVIDER_METHODS.GOOGLE)
                    }
                    variant={BUTTON_VARIANT.OUTLINE}
                    icon={Icons.Google}
                  >
                    Sign up with Google
                  </Button>
                </Box>
                <Text
                  as="p"
                  color={COLOR_INTENT.SIGN_IN.TEXT}
                  fontSize={FONT_SIZE.PX_12}
                  m={`${SPACE.PX_10} 0`}
                  textAlign="center"
                >
                  or sign up using email
                </Text>
                <Box>
                  <Field name="email">
                    {({ field, meta }) => (
                      <Input
                        label="Email"
                        isDisabled={userAuth.isLoading}
                        isError={meta.touched && meta.error !== undefined}
                        hint={meta.touched ? meta.error : ''}
                        hasHint
                        {...field}
                      />
                    )}
                  </Field>
                  <Button
                    isDisabled={userAuth.isLoading || !values.email}
                    isLoading={userAuth.isLoading}
                    type="submit"
                    mt={SPACE.PX_4}
                  >
                    Continue
                  </Button>
                </Box>
                <Text
                  color={COLOR_INTENT.SIGN_IN.TEXT}
                  fontSize={FONT_SIZE.PX_12}
                  mt={SPACE.PX_15}
                  textAlign="center"
                  as="p"
                >
                  Already have client account?{' '}
                  <Text
                    as="span"
                    role="button"
                    color={COLOR_NAMES.PINEY_LAKE}
                    fontSize={FONT_SIZE.PX_12}
                    onClick={() => setIsSignInActive(true)}
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Sign in.
                  </Text>
                </Text>
              </Form>
            )}
          </Formik>
        </Box>
      )}
    </Box>
  )
}
